import { useNuxtApp } from '#app'
import { useMainStore } from '@/composables/store'
import { getCurrentServer } from '@/utils/globalData'
import {
  getSavedCurrencyCode,
  LANGUAGE_CACHE_KEY,
  resolveInitialLocaleCurrencyPreference,
} from '@/utils/ipLocaleCurrencyPreference'

let automaticPreferencePromise = null

export function useLanguageDetection() {
  const { $i18n } = useNuxtApp()
  const store = useMainStore()

  const supportedLocales = ['zh-CN', 'zh-TW', 'vi_VN', 'en', 'ja_JP']
  const browserLanguageMap = {
    'zh': 'zh-CN',
    'zh-CN': 'zh-CN',
    'zh-TW': 'zh-TW',
    'zh-HK': 'zh-TW',
    'vi': 'vi_VN',
    'vi-VN': 'vi_VN',
    'en': 'en',
    'en-US': 'en',
    'en-GB': 'en',
    'ja': 'ja_JP',
    'ja-JP': 'ja_JP',
  }

  const detectBrowserLanguage = () => {
    if (import.meta.server)
      return null
    const browserLanguages = navigator.languages || [navigator.language]
    for (const language of browserLanguages) {
      if (supportedLocales.includes(language))
        return language
      const mappedLanguage = browserLanguageMap[language]
      if (mappedLanguage && supportedLocales.includes(mappedLanguage)) {
        return mappedLanguage
      }
      const mappedLanguageCode = browserLanguageMap[language.split('-')[0]]
      if (mappedLanguageCode && supportedLocales.includes(mappedLanguageCode)) {
        return mappedLanguageCode
      }
    }
    return 'zh-CN'
  }

  const isFirstVisit = () => {
    if (import.meta.server)
      return false
    return !localStorage.getItem(LANGUAGE_CACHE_KEY)
      && !localStorage.getItem('has_visited')
  }

  const persistLanguage = async (locale) => {
    if (import.meta.server || !supportedLocales.includes(locale))
      return
    if (locale !== $i18n.locale.value) {
      await $i18n.setLocale(locale)
    }
    if (typeof $i18n.setLocaleCookie === 'function') {
      $i18n.setLocaleCookie(locale)
    }
    localStorage.setItem(LANGUAGE_CACHE_KEY, locale)
    localStorage.setItem('has_visited', 'true')
  }

  const autoSwitchLanguage = async () => {
    if (import.meta.server)
      return null
    if (automaticPreferencePromise)
      return automaticPreferencePromise

    automaticPreferencePromise = (async () => {
      const currentServer = getCurrentServer()
      const storeInfo = store.storeInfoDataMap[currentServer] || {}
      const savedLanguage = localStorage.getItem(LANGUAGE_CACHE_KEY) || ''
      const savedCurrencyCode = getSavedCurrencyCode(localStorage)
      const preference = await resolveInitialLocaleCurrencyPreference({
        storeInfo,
        savedLanguage,
        savedCurrencyCode,
      })

      if (preference) {
        if (!savedLanguage) {
          await persistLanguage(preference.language)
        }
        if (!savedCurrencyCode) {
          try {
            await store.getCurrencyData(preference.currencyCode)
          }
          catch (error) {
            console.warn('Failed to initialize currency preference:', error)
          }
        }
        return preference
      }

      if (!isFirstVisit())
        return null
      const detectedLanguage = store.getMallSiteDefaultLocale()
        || detectBrowserLanguage()
      await persistLanguage(detectedLanguage)
      return { language: detectedLanguage, source: 'legacy-default' }
    })()

    try {
      return await automaticPreferencePromise
    }
    finally {
      automaticPreferencePromise = null
    }
  }

  const switchLanguage = async (locale) => {
    await persistLanguage(locale)
  }

  return {
    detectBrowserLanguage,
    isFirstVisit,
    autoSwitchLanguage,
    switchLanguage,
    supportedLocales,
  }
}
